import { FolderAddOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useAuthContext } from 'contexts/AuthContext';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { AccessType } from 'types/User';
import AjoutDossierForm from 'views/dossiers/ajout/AjoutDossierForm';
import NouveauDossierConcessionnaire from 'views/dossiers/concessionnaire/NouveauDossierConcessionnaire.page';

type ConcessionnaireDebugPreset = 'debug-step1' | 'debug-step3';

const NewDossierButton: React.FC = () => {
  const { t } = useTranslation();
  const { auth } = useAuthContext();
  const location = useLocation();
  const [openRequestId, setOpenRequestId] = useState(0);
  const [isConcessionnaireModalOpen, setIsConcessionnaireModalOpen] =
    useState(false);
  const [concessionnaireInitialStep, setConcessionnaireInitialStep] =
    useState(1);
  const [concessionnaireDebugPreset, setConcessionnaireDebugPreset] = useState<
    ConcessionnaireDebugPreset | undefined
  >(undefined);

  const isConcessionnaire =
    auth?.user?.accessType === AccessType.CONCESSIONNAIRE;
  const isSimulateur = auth?.user?.accessType === AccessType.SIMULATEUR;
  const isClient = auth?.user?.accessType === AccessType.CLIENT;

  const handleOpenModal = () => {
    if (isClient) {
      setOpenRequestId((previous) => previous + 1);
      return;
    }

    if (isConcessionnaire) {
      setConcessionnaireInitialStep(1);
      setConcessionnaireDebugPreset(undefined);
      setIsConcessionnaireModalOpen(true);
    }
  };

  useEffect(() => {
    if (!isConcessionnaire) {
      return;
    }

    const query = new URLSearchParams(location.search);
    if (query.get('openNouveauDossier') !== '1') {
      return;
    }

    const rawStep = Number(query.get('nouveauDossierStep'));
    const normalizedStep =
      Number.isFinite(rawStep) && rawStep >= 1 && rawStep <= 3
        ? Math.floor(rawStep)
        : 1;
    const rawPreset = query.get('nouveauDossierPreset');
    const preset =
      rawPreset === 'debug-step1' || rawPreset === 'debug-step3'
        ? rawPreset
        : undefined;

    setConcessionnaireInitialStep(normalizedStep);
    setConcessionnaireDebugPreset(preset);
    setIsConcessionnaireModalOpen(true);
  }, [isConcessionnaire, location.search]);

  const buttonContent = (
    <Button
      type="primary"
      size="large"
      shape="round"
      icon={<FolderAddOutlined />}
      onClick={isClient || isConcessionnaire ? handleOpenModal : undefined}
    >
      {isSimulateur
        ? t('global:calculer_ma_prime')
        : t('global:soumettre_un_dossier')}
    </Button>
  );

  if (isClient) {
    return (
      <>
        {buttonContent}
        <AjoutDossierForm openRequestId={openRequestId} />
      </>
    );
  }

  if (isConcessionnaire) {
    return (
      <>
        {buttonContent}
        <NouveauDossierConcessionnaire
          open={isConcessionnaireModalOpen}
          initialStep={concessionnaireInitialStep}
          debugPreset={concessionnaireDebugPreset}
          onClose={() => setIsConcessionnaireModalOpen(false)}
        />
      </>
    );
  }

  return (
    <Link to={isSimulateur ? '/calculer-prime' : '#'}>{buttonContent}</Link>
  );
};

export default NewDossierButton;
